import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Flame, LogOut, Loader2, Trash2, Plus, Pencil, X, RefreshCw, Search, Download as DownloadIcon, Upload, Music, CheckCircle2, CreditCard } from 'lucide-react';
import {
  adminLogin, setAdminToken, getAdminToken, clearAdminToken,
  adminStats, adminContacts, adminBookings, adminFireside, adminSubscribers,
  adminRsvps, adminOrders, getEvents, adminCreateEvent, adminUpdateEvent, adminDeleteEvent,
  adminDeleteRecord, adminDigital, adminDigitalUpload, adminDigitalDelete, adminRebuildStripeCatalog,
} from '../lib/apiClient';
import { useToast } from '../hooks/use-toast';

const TABS = ['Overview', 'Bookings', 'Fireside', 'Messages', 'Events', 'RSVPs', 'Subscribers', 'Orders', 'Downloads'];

const emptyEvent = { title: '', city: '', venue: '', date: '', time: '', type: 'After the Fire LIVE', description: '', spots: 0, status: 'upcoming' };

const Admin = () => {
  const { toast } = useToast();
  const [authed, setAuthed] = useState(!!getAdminToken());
  const [pw, setPw] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);
  const [tab, setTab] = useState('Overview');

  const [stats, setStats] = useState(null);
  const [data, setData] = useState({ bookings: [], fireside: [], contacts: [], events: [], rsvps: [], subscribers: [], orders: [], digital: [] });
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // event being created/edited

  const login = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      const { token } = await adminLogin(pw);
      setAdminToken(token);
      setAuthed(true);
      toast({ title: 'Welcome back, Dr. Gee 🔥' });
    } catch (err) {
      toast({ title: 'Incorrect password', description: 'Please try again.' });
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = () => {
    clearAdminToken();
    setAuthed(false);
    setPw('');
  };

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, b, f, c, e, r, sub, o, dg] = await Promise.all([
        adminStats(), adminBookings(), adminFireside(), adminContacts(),
        getEvents(), adminRsvps(), adminSubscribers(), adminOrders(), adminDigital(),
      ]);
      setStats(s);
      setData({ bookings: b, fireside: f, contacts: c, events: e, rsvps: r, subscribers: sub, orders: o, digital: dg });
    } catch (err) {
      if (err?.response?.status === 401) {
        logout();
        toast({ title: 'Session expired', description: 'Please log in again.' });
      } else {
        toast({ title: 'Could not load data' });
      }
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    if (authed) loadAll();
  }, [authed, loadAll]);

  const saveEvent = async (e) => {
    e.preventDefault();
    const payload = { ...editing, spots: Number(editing.spots) || 0 };
    try {
      if (editing.id) {
        await adminUpdateEvent(editing.id, payload);
        toast({ title: 'Event updated' });
      } else {
        await adminCreateEvent(payload);
        toast({ title: 'Event created' });
      }
      setEditing(null);
      loadAll();
    } catch (err) {
      toast({ title: 'Could not save event' });
    }
  };

  const removeEvent = async (id) => {
    try {
      await adminDeleteEvent(id);
      toast({ title: 'Event deleted' });
      loadAll();
    } catch (err) {
      toast({ title: 'Could not delete event' });
    }
  };

  const deleteRecord = async (coll, id) => {
    try {
      await adminDeleteRecord(coll, id);
      toast({ title: 'Deleted' });
      loadAll();
    } catch (err) {
      toast({ title: 'Could not delete' });
    }
  };

  // ---------- Login screen ----------
  if (!authed) {
    return (
      <div className="min-h-screen ember-texture flex items-center justify-center px-6">
        <form onSubmit={login} className="card-dark rounded-2xl p-9 max-w-sm w-full text-center">
          <Flame className="w-10 h-10 text-fire-gold mx-auto mb-5 animate-flicker" strokeWidth={1.3} />
          <h1 className="font-display text-3xl text-fire-cream">Backoffice</h1>
          <p className="text-fire-cream/55 font-body text-sm mt-2 mb-6">After the Fire™ admin dashboard</p>
          <input
            type="password"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            placeholder="Admin password"
            className="w-full bg-fire-ink/70 border border-fire-gold/25 rounded-lg px-4 py-3 text-fire-cream placeholder:text-fire-cream/35 focus:outline-none focus:border-fire-gold transition-colors mb-4"
          />
          <button type="submit" disabled={loggingIn} className="btn-gold w-full rounded-full py-3.5 font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-70">
            {loggingIn ? (<><Loader2 className="w-4 h-4 animate-spin" /> Signing in…</>) : 'Sign In'}
          </button>
        </form>
      </div>
    );
  }

  // ---------- Dashboard ----------
  return (
    <div className="min-h-screen bg-fire-ink pt-24 pb-20">
      <div className="max-w-[1300px] mx-auto px-5 lg:px-8">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <Flame className="w-7 h-7 text-fire-gold" strokeWidth={1.5} />
            <h1 className="font-display text-3xl text-fire-cream">Backoffice</h1>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={loadAll} className="btn-outline-gold rounded-full px-4 py-2 text-sm font-semibold inline-flex items-center gap-2">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </button>
            <button onClick={logout} className="rounded-full px-4 py-2 text-sm font-semibold inline-flex items-center gap-2 border border-fire-maroon/60 text-fire-cream/80 hover:bg-fire-maroon/30 transition-colors">
              <LogOut className="w-4 h-4" /> Log out
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-8 border-b border-fire-gold/15 pb-4">
          {TABS.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`rounded-full px-4 py-2 text-sm font-body font-medium transition-all ${
                tab === t ? 'btn-gold' : 'border border-fire-gold/20 text-fire-cream/65 hover:text-fire-goldlight hover:border-fire-gold'
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {loading && !stats ? (
          <div className="flex justify-center py-20"><Loader2 className="w-10 h-10 text-fire-gold animate-spin" /></div>
        ) : (
          <>
            {tab === 'Overview' && stats && (
              <div className="space-y-6">
                <StripeCatalogPanel toast={toast} />
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  ['Revenue', `$${((stats.revenue || 0) / 100).toFixed(2)}`],
                  ['Paid Orders', stats.orders_paid],
                  ['Bookings', stats.bookings], ['Fireside Requests', stats.fireside],
                  ['Messages', stats.contacts], ['Subscribers', stats.subscribers],
                  ['Events', stats.events], ['RSVPs', stats.rsvps],
                ].map(([label, val]) => (
                  <div key={label} className="card-dark rounded-2xl p-6" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
                    <p className="font-display text-4xl text-gradient-gold">{val}</p>
                    <p className="font-marcellus tracking-widest text-fire-cream/60 text-xs mt-2 uppercase">{label}</p>
                  </div>
                ))}
                </div>
              </div>
            )}

            {tab === 'Bookings' && <Table rows={data.bookings} coll="bookings" onDelete={deleteRecord} name="bookings" cols={[['created_at', 'Date'], ['name', 'Name'], ['email', 'Email'], ['org', 'Organization'], ['pkg', 'Package'], ['eventType', 'Type'], ['message', 'Message']]} />}
            {tab === 'Fireside' && <Table rows={data.fireside} coll="fireside" onDelete={deleteRecord} name="fireside" cols={[['created_at', 'Date'], ['name', 'Name'], ['email', 'Email'], ['plan', 'Option'], ['message', 'Message']]} />}
            {tab === 'Messages' && <Table rows={data.contacts} coll="contacts" onDelete={deleteRecord} name="messages" cols={[['created_at', 'Date'], ['name', 'Name'], ['email', 'Email'], ['subject', 'Subject'], ['message', 'Message']]} />}
            {tab === 'RSVPs' && <Table rows={data.rsvps} coll="rsvps" onDelete={deleteRecord} name="rsvps" cols={[['created_at', 'Date'], ['event_title', 'Event'], ['name', 'Name'], ['email', 'Email'], ['guests', 'Guests']]} />}
            {tab === 'Subscribers' && <Table rows={data.subscribers} coll="subscribers" onDelete={deleteRecord} name="subscribers" cols={[['created_at', 'Date'], ['email', 'Email']]} />}
            {tab === 'Orders' && <Table rows={data.orders} name="orders" cols={[['created_at', 'Date'], ['items', 'Items'], ['amount', 'Amount'], ['payment_status', 'Status'], ['shipping', 'Ship To']]} money />}

            {tab === 'Downloads' && <DownloadsManager items={data.digital} onChanged={loadAll} toast={toast} />}

            {tab === 'Events' && (
              <div>
                <div className="flex justify-end mb-5">
                  <button onClick={() => setEditing({ ...emptyEvent })} className="btn-gold rounded-full px-5 py-2.5 text-sm font-semibold inline-flex items-center gap-2">
                    <Plus className="w-4 h-4" /> New Event
                  </button>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  {data.events.map((ev) => (
                    <div key={ev.id} className="card-dark rounded-xl p-5">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-display text-lg text-fire-cream">{ev.title}</p>
                          <p className="text-fire-cream/55 font-body text-sm">{ev.city} · {ev.date} · {ev.time}</p>
                          <p className="text-fire-goldlight font-body text-xs mt-1">{ev.type} · {ev.spots} seats</p>
                        </div>
                        <div className="flex gap-2">
                          <button onClick={() => setEditing(ev)} className="p-2 rounded-lg border border-fire-gold/25 text-fire-cream/70 hover:text-fire-goldlight"><Pencil className="w-4 h-4" /></button>
                          <button onClick={() => removeEvent(ev.id)} className="p-2 rounded-lg border border-fire-maroon/40 text-fire-cream/60 hover:text-fire-maroon"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Event editor modal */}
      {editing && (
        <div className="fixed inset-0 z-[95] flex items-center justify-center px-5 py-8 overflow-y-auto">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setEditing(null)} />
          <form onSubmit={saveEvent} className="relative card-dark rounded-2xl p-7 max-w-lg w-full my-auto">
            <button type="button" onClick={() => setEditing(null)} className="absolute top-4 right-4 text-fire-cream/50 hover:text-fire-goldlight"><X className="w-5 h-5" /></button>
            <h3 className="font-display text-2xl text-fire-cream mb-5">{editing.id ? 'Edit Event' : 'New Event'}</h3>
            <div className="grid grid-cols-2 gap-4">
              <EField label="Title" val={editing.title} on={(v) => setEditing({ ...editing, title: v })} span />
              <EField label="City" val={editing.city} on={(v) => setEditing({ ...editing, city: v })} />
              <EField label="Venue" val={editing.venue} on={(v) => setEditing({ ...editing, venue: v })} />
              <EField label="Date" type="date" val={editing.date} on={(v) => setEditing({ ...editing, date: v })} />
              <EField label="Time" val={editing.time} on={(v) => setEditing({ ...editing, time: v })} />
              <EField label="Type" val={editing.type} on={(v) => setEditing({ ...editing, type: v })} />
              <EField label="Seats" type="number" val={editing.spots} on={(v) => setEditing({ ...editing, spots: v })} />
              <div className="col-span-2">
                <label className="block font-body text-sm text-fire-cream/70 mb-2">Description</label>
                <textarea value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} rows={3} className="w-full bg-fire-ink/70 border border-fire-gold/25 rounded-lg px-4 py-3 text-fire-cream focus:outline-none focus:border-fire-gold transition-colors resize-none" />
              </div>
            </div>
            <button type="submit" className="btn-gold w-full rounded-full py-3.5 font-semibold mt-6">Save Event</button>
          </form>
        </div>
      )}
    </div>
  );
};

const EField = ({ label, val, on, type = 'text', span }) => (
  <div className={span ? 'col-span-2' : ''}>
    <label className="block font-body text-sm text-fire-cream/70 mb-2">{label}</label>
    <input type={type} value={val} onChange={(e) => on(e.target.value)} className="w-full bg-fire-ink/70 border border-fire-gold/25 rounded-lg px-4 py-2.5 text-fire-cream focus:outline-none focus:border-fire-gold transition-colors" />
  </div>
);

const fmt = (v, key, money) => {
  if (v == null || v === '') return '—';
  if (key === 'created_at') return new Date(v).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  if (key === 'amount' && money) return `$${(v / 100).toFixed(2)}`;
  if (key === 'shipping' && typeof v === 'object') {
    const a = v.address || {};
    const parts = [v.name, a.line1, a.line2, a.city, a.state, a.postal_code, a.country].filter(Boolean);
    return parts.length ? parts.join(', ') : '—';
  }
  if (Array.isArray(v)) return v.join(', ');
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

const toCsv = (rows, cols, money) => {
  const header = cols.map(([, label]) => `"${label}"`).join(',');
  const lines = rows.map((row) =>
    cols.map(([k]) => `"${String(fmt(row[k], k, money)).replace(/"/g, '""')}"`).join(',')
  );
  return [header, ...lines].join('\n');
};

const Table = ({ rows, cols, money, coll, onDelete, name = 'data' }) => {
  const [q, setQ] = useState('');
  const filtered = !q
    ? rows
    : rows.filter((row) => cols.some(([k]) => String(fmt(row[k], k, money)).toLowerCase().includes(q.toLowerCase())));

  const exportCsv = () => {
    const csv = toCsv(filtered, cols, money);
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `atf-${name}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!rows || rows.length === 0) {
    return <p className="text-fire-cream/50 font-cormorant text-xl text-center py-16">Nothing here yet.</p>;
  }

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search className="w-4 h-4 text-fire-cream/40 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={`Search ${name}…`}
            data-testid={`search-${name}`}
            className="w-full bg-fire-ink/70 border border-fire-gold/25 rounded-full pl-9 pr-4 py-2 text-sm text-fire-cream placeholder:text-fire-cream/35 focus:outline-none focus:border-fire-gold transition-colors"
          />
        </div>
        <div className="flex items-center gap-3">
          <span className="text-fire-cream/45 font-body text-xs">{filtered.length} of {rows.length}</span>
          <button onClick={exportCsv} data-testid={`export-${name}`} className="btn-outline-gold rounded-full px-4 py-2 text-sm font-semibold inline-flex items-center gap-2">
            <DownloadIcon className="w-4 h-4" /> CSV
          </button>
        </div>
      </div>
      <div className="overflow-x-auto card-dark rounded-2xl">
        <table className="w-full text-left">
          <thead>
            <tr className="border-b border-fire-gold/15">
              {cols.map(([k, label]) => (
                <th key={k} className="font-marcellus tracking-widest text-fire-gold text-[11px] uppercase px-4 py-3 whitespace-nowrap">{label}</th>
              ))}
              {coll && <th className="px-4 py-3" />}
            </tr>
          </thead>
          <tbody>
            {filtered.map((row, i) => (
              <tr key={row.id || i} className="border-b border-fire-gold/8 hover:bg-fire-gold/5">
                {cols.map(([k]) => (
                  <td key={k} className="px-4 py-3 text-fire-cream/75 font-body text-sm max-w-[280px] truncate">{fmt(row[k], k, money)}</td>
                ))}
                {coll && (
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => onDelete(coll, row.id)}
                      data-testid={`delete-${name}-${row.id}`}
                      className="p-2 rounded-lg border border-fire-maroon/40 text-fire-cream/50 hover:text-fire-maroon transition-colors"
                      aria-label="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const fmtSize = (b) => (b > 1024 * 1024 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`);

const StripeCatalogPanel = ({ toast }) => {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const rebuild = async () => {
    setBusy(true);
    setResult(null);
    try {
      const r = await adminRebuildStripeCatalog();
      setResult(r);
      toast({ title: `Catalog synced to ${r.mode.toUpperCase()} account`, description: `${r.created.length} created, ${r.already_ok.length} already set.` });
    } catch (err) {
      toast({ title: 'Rebuild failed', description: err?.response?.data?.detail || 'Check your Stripe key.' });
    } finally {
      setBusy(false);
    }
  };

  const isLive = result?.mode === 'live';

  return (
    <div className="card-dark rounded-2xl p-6 border border-fire-gold/20" data-testid="stripe-catalog-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="max-w-xl">
          <div className="flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-fire-goldlight" />
            <h3 className="font-display text-xl text-fire-cream">Stripe Catalog</h3>
          </div>
          <p className="font-body text-fire-cream/60 text-sm mt-2">
            Creates every shop product &amp; price on the Stripe account this app is running with.
            Run this once after switching to your <span className="text-fire-goldlight">live keys</span> so live checkout can find your products. Safe to run anytime.
          </p>
        </div>
        <button
          onClick={rebuild}
          disabled={busy}
          data-testid="rebuild-catalog-btn"
          className="btn-gold rounded-full px-5 py-3 text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-70 shrink-0"
        >
          {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Rebuilding…</> : <><RefreshCw className="w-4 h-4" /> Rebuild Catalog</>}
        </button>
      </div>
      {result && (
        <div className="mt-5 rounded-xl bg-fire-ink/60 border border-fire-gold/15 p-4" data-testid="rebuild-result">
          <div className="flex items-center gap-2 mb-2">
            <span className={`px-2.5 py-1 rounded-full text-[11px] font-semibold tracking-wide ${isLive ? 'bg-green-500/20 text-green-300 border border-green-500/40' : 'bg-fire-gold/15 text-fire-goldlight border border-fire-gold/30'}`}>
              {isLive ? 'LIVE MODE' : 'TEST MODE'}
            </span>
            {result.account_id && <span className="font-body text-fire-cream/50 text-xs">{result.account_id}</span>}
          </div>
          <p className="font-body text-fire-cream/80 text-sm">
            {result.total} products synced — <span className="text-fire-goldlight">{result.created.length} newly created</span>, {result.already_ok.length} already set up.
          </p>
        </div>
      )}
    </div>
  );
};

const DownloadsManager = ({ items, onChanged, toast }) => {
  const [busy, setBusy] = useState(null);
  const [progress, setProgress] = useState(0);
  const fileRefs = useRef({});

  const upload = async (slug, file) => {
    if (!file) return;
    setBusy(slug);
    setProgress(0);
    try {
      await adminDigitalUpload(slug, file, setProgress);
      toast({ title: 'Full track uploaded 🔥' });
      onChanged();
    } catch (err) {
      toast({ title: 'Upload failed', description: err?.response?.data?.detail || 'Please try an audio file.' });
    } finally {
      setBusy(null);
      setProgress(0);
    }
  };

  const remove = async (slug) => {
    try {
      await adminDigitalDelete(slug);
      toast({ title: 'Reverted to 30s preview' });
      onChanged();
    } catch (err) {
      toast({ title: 'Could not remove' });
    }
  };

  return (
    <div>
      <div className="card-dark rounded-2xl p-5 mb-5 border border-fire-gold/15">
        <p className="text-fire-cream/70 font-body text-sm">
          Upload the <span className="text-fire-goldlight">full-length MP3</span> for each song. Buyers receive secure download
          links after checkout. Until a full file is uploaded, the 30-second preview is delivered as a placeholder.
        </p>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {items.map((it) => (
          <div key={it.slug} className="card-dark rounded-xl p-5 flex items-center justify-between gap-4" data-testid={`digital-row-${it.slug}`}>
            <div className="flex items-center gap-3 min-w-0">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${it.uploaded ? 'bg-fire-gold text-fire-ink' : 'border border-fire-gold/30 text-fire-cream/60'}`}>
                {it.uploaded ? <CheckCircle2 className="w-5 h-5" /> : <Music className="w-4 h-4" />}
              </div>
              <div className="min-w-0">
                <p className="font-cormorant text-lg text-fire-cream truncate">{it.title}</p>
                <p className="font-body text-xs text-fire-cream/50 truncate">
                  {it.uploaded ? `Full track · ${fmtSize(it.size)}` : 'Preview placeholder (30s)'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <input
                type="file"
                accept="audio/*"
                className="hidden"
                ref={(el) => (fileRefs.current[it.slug] = el)}
                onChange={(e) => upload(it.slug, e.target.files[0])}
                data-testid={`upload-input-${it.slug}`}
              />
              <button
                onClick={() => fileRefs.current[it.slug]?.click()}
                disabled={busy === it.slug}
                data-testid={`upload-btn-${it.slug}`}
                className="btn-gold rounded-full px-4 py-2 text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-70"
              >
                {busy === it.slug
                  ? (<><Loader2 className="w-3.5 h-3.5 animate-spin" /> {progress}%</>)
                  : (<><Upload className="w-3.5 h-3.5" /> {it.uploaded ? 'Replace' : 'Upload'}</>)}
              </button>
              {it.uploaded && (
                <button onClick={() => remove(it.slug)} data-testid={`digital-delete-${it.slug}`} className="p-2 rounded-lg border border-fire-maroon/40 text-fire-cream/50 hover:text-fire-maroon transition-colors" aria-label="Remove full track">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Admin;
